import React, { useState, useEffect } from 'react';
import { Send, AlertTriangle, CheckCircle2, Info, Loader2, FileText, Calculator, Building2 } from 'lucide-react';
import { useTrialStore } from '../store/trialStore';
import { useSubscriptionStore } from '../store/subscriptionStore';
import { supabase } from '../lib/supabase';

interface ChatInterfaceProps {
  onClose: () => void;
}

export default function ChatInterface({ onClose }: ChatInterfaceProps) {
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { questionsLeft, messages, decrementQuestions, addMessage } = useTrialStore();
  const { subscription } = useSubscriptionStore();
  const [activeAnalysis, setActiveAnalysis] = useState<'document' | 'viability' | null>(null);

  const isPremium = subscription?.status === 'active';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || (!isPremium && questionsLeft <= 0) || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    addMessage('user', userMessage);
    if (!isPremium) decrementQuestions();
    setIsLoading(true);

    try {
      // Determine analysis type
      if (userMessage.toLowerCase().includes('edital') || userMessage.toLowerCase().includes('matrícula')) {
        setActiveAnalysis('document');
      } else if (userMessage.toLowerCase().includes('viabilidade') || userMessage.toLowerCase().includes('calcul')) {
        setActiveAnalysis('viability');
      }

      const { data, error } = await supabase.functions.invoke('chat', {
        body: { 
          messages: [
            ...messages,
            { role: 'user', content: userMessage }
          ]
        }
      });

      if (error) throw error;

      addMessage('assistant', data.message);
    } catch (error) {
      console.error('Error:', error);
      addMessage('assistant', 'Desculpe, ocorreu um erro ao processar sua mensagem. Por favor, tente novamente.');
    } finally {
      setIsLoading(false);
      setActiveAnalysis(null);
    }
  };

  const suggestedQuestions = [
    {
      text: "Analise este edital de leilão para mim",
      icon: <FileText className="h-4 w-4" />
    },
    {
      text: "Calcule a viabilidade deste imóvel",
      icon: <Calculator className="h-4 w-4" />
    },
    {
      text: "Busque imóveis com desconto acima de 30%",
      icon: <Building2 className="h-4 w-4" />
    }
  ];

  return (
    <div className="fixed inset-0 bg-white z-50 flex flex-col">
      <div className="bg-blue-600 text-white p-4 flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">ImóvelIA Assistente</h2>
          <p className="text-sm text-blue-100">
            {isPremium ? 'Versão Premium' : `Versão de teste • ${questionsLeft} perguntas restantes`}
          </p>
        </div>
        <button onClick={onClose} className="text-white hover:text-blue-200">
          Fechar
        </button>
      </div>

      {activeAnalysis && (
        <div className="bg-blue-50 p-4 border-b border-blue-100">
          <div className="flex items-center gap-3 max-w-3xl mx-auto">
            <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
            <span className="text-blue-700">
              {activeAnalysis === 'document' ? 'Analisando documento...' : 'Calculando viabilidade...'}
            </span>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-lg p-4 ${
                message.role === 'user'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              {message.content}
              {!isPremium && message.role === 'assistant' && questionsLeft < 5 && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center gap-2 text-sm text-blue-600">
                    <AlertTriangle className="h-4 w-4" />
                    <span>
                      Versão de teste limitada. Para análise completa, 
                      <a 
                        href="https://pay.kiwify.com.br/cSvs3ux"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline ml-1"
                      >
                        assine a versão completa
                      </a>
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 text-gray-800 rounded-lg p-4">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          </div>
        )}
      </div>

      <div className="border-t p-4 bg-white">
        {(isPremium || questionsLeft > 0) ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="flex gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Digite sua pergunta sobre leilões..."
                className="flex-1 border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isLoading}
              />
              <button
                type="submit"
                disabled={isLoading}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg disabled:opacity-50"
              >
                <Send className="h-5 w-5" />
              </button>
            </div>

            {messages.length === 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {suggestedQuestions.map((question, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setInput(question.text)}
                    className="flex items-center gap-2 p-2 text-sm text-left text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
                  >
                    {question.icon}
                    <span>{question.text}</span>
                  </button>
                ))}
              </div>
            )}

            {!isPremium && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Info className="h-4 w-4" />
                <span>
                  Versão de teste: análises básicas e dicas gerais. 
                  Para análise completa de documentos, assine a versão completa.
                </span>
              </div>
            )}
          </form>
        ) : (
          <div className="text-center space-y-6">
            <div className="bg-blue-50 p-6 rounded-lg">
              <CheckCircle2 className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">
                Experimente o poder total do ImóvelIA
              </h3>
              <p className="text-slate-600 mb-6">
                Na versão completa você tem acesso a:
              </p>
              <ul className="text-left space-y-3 max-w-md mx-auto mb-6">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  <span>Análise completa com OpenAI Assistants API</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  <span>Code Interpreter para cálculos avançados</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  <span>File Search em documentos complexos</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  <span>Assistente personalizado ilimitado</span>
                </li>
              </ul>
              <a
                href="https://pay.kiwify.com.br/cSvs3ux"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
              >
                Assinar Versão Completa
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}